import React, { useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Grid2
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import logo from '../Malayan.png';
import { changePassword, uploadSignature } from "../utility/api";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { dataURLToBlob, processSignatureDataUrl } from "../utility/utils";

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShowCanvas, setIsShowCanvas] = useState(false);
  const user = useSelector((state) => state.user.user);
 const claimantSigRef = useRef(null);

  const clearClaimantSignature = () => claimantSigRef.current.clear();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const submitSignature = async () => {
        try{
          const rawSignature = claimantSigRef.current.toDataURL('image/png');
          // Normalise to the same 400x150 canvas the main signature dialog produces.
          // Uploading the raw canvas here meant a first-login signature landed in the PDF
          // at whatever size (and device pixel ratio) this screen happened to use.
          const signatureImage =
            (await processSignatureDataUrl(rawSignature, {
              padding: 20,
              targetWidth: 400,
              targetHeight: 150,
              alphaThreshold: 10,
              whiteThreshold: 250,
              background: null,
            })) || rawSignature;
          const formData = new FormData();
          formData.append("signature", dataURLToBlob(signatureImage), "signature.png");
          formData.append("userName", user.userName);
          const response = await uploadSignature(formData);
          console.log(response);
          if(response.statusCode === "OK"){
            setIsShowCanvas(false);
          }
        } catch (error) {
          console.error("Error generating or uploading PDF:", error);
        }
      }

  const validate = () => {
    let validationErrors = {};

    if (!formData.currentPassword.trim()) {
      validationErrors.currentPassword = "Current password is required.";
    }
    if (!formData.newPassword.trim()) {
      validationErrors.newPassword = "New password is required.";
    } else if (formData.newPassword.length < 8) {
      validationErrors.newPassword = "Password must be at least 8 characters long.";
    }
    if (formData.confirmPassword !== formData.newPassword) {
      validationErrors.confirmPassword = "Passwords do not match.";
    }

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);

      try {
        // Call the API to change the password
        const response = await changePassword(
            user.userName,
          formData.currentPassword,
          formData.newPassword
        );
        console.log("Password changed successfully:", response);
        alert("Password changed successfully!");
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        if(user.isSignatureAvailable){
          navigate('/home');
        } else {
          setIsShowCanvas(true);
        }
      } catch (error) {
        console.error("Failed to change password:", error);
        alert("Failed to change password. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  return (
    <Box sx={{
      width: '100%',
      margin:'auto',
      paddingBottom: 3
    }}>
        <AppBar position="static">
        <Toolbar>
          <Avatar src={logo} />
          <Typography variant="h6" sx={{ flexGrow: 1, alignItems: 'left' }}>
            Electronic Malayan Adjustment System (eMACS)
          </Typography>
        </Toolbar>
      </AppBar>
      <Container component="main" maxWidth="xs">
        <Card
          sx={{
            marginTop: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#e8e8e8',
            padding: 5,
          }}
        >
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Change Password
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Current Password"
              name="currentPassword"
              type="password"
              fullWidth
              required
              margin="normal"
              value={formData.currentPassword}
              onChange={handleChange}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
            />
            <TextField
              label="New Password"
              name="newPassword"
              type="password"
              fullWidth
              required
              margin="normal"
              value={formData.newPassword}
              onChange={handleChange}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
            />
            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              fullWidth
              required
              margin="normal"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Changing Password..." : "Change Password"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
    <Dialog open={isShowCanvas} onClose={() => setIsShowCanvas(false)} maxWidth="sm" fullWidth>
        <DialogTitle variant="h4" align="center" gutterBottom>
          Signature
          <IconButton
            aria-label="close"
            onClick={() => setIsShowCanvas(false)}
            style={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
        <Grid2 item xs={12} sm={6}>
                    <Typography variant="h6" align="center">
                      Signature
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        border: '1px solid #ccc',
                        width: '100%',
                        height: '150px',
                      }}
                    >
                      <SignatureCanvas
                        ref={claimantSigRef}
                        penColor="black"
                        canvasProps={{
                          width: 300,
                          height: 150,
                          style: { maxWidth: '100%' },
                        }}
                      />
                    </Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={clearClaimantSignature}
                    >
                      Clear
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={submitSignature}
                    >
                      Submit
                    </Button>
                  </Grid2>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChangePassword;